import { useState, useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function ContactPage() {
  useEffect(() => {
    const schema = {
      "@context": "https://schema.org",
      "@type": "ContactPage",
      "@id": `${import.meta.env.VITE_SITE_URL}/contact`,
      "url": `${import.meta.env.VITE_SITE_URL}/contact`,
      "name": "Contactez JLD Concept - Télécom & Énergie Belgique | Herstal",
      "description": "Contactez Danko Jean-Luc, Président Manager de JLD Concept. Téléphone : +32 498 66 42 15. Email : jld@danko.be. Rue de l'Abbaye 5, 4040 Herstal, Belgique.",
      "inLanguage": "fr-BE",
      "isPartOf": { "@id": `${import.meta.env.VITE_SITE_URL}/#website` },
      "mainEntity": {
        "@type": "Person",
        "name": "Danko Jean-Luc",
        "jobTitle": "Président Manager",
        "telephone": "+32498664215",
        "email": "jld@danko.be",
        "worksFor": {
          "@type": "Organization",
          "name": "JLD Concept SRL"
        },
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "Rue de l'Abbaye 5",
          "addressLocality": "Herstal",
          "postalCode": "4040",
          "addressCountry": "BE"
        }
      }
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-contact';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);
    return () => { document.getElementById('schema-contact')?.remove(); };
  }, []);

  const [formData, setFormData] = useState({
    nom: '',
    email: '',
    telephone: '',
    sujet: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d55frlvq1d2qkrs09dr0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          nom: '',
          email: '',
          telephone: '',
          sujet: '',
          message: '',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <Navbar />

      <div className="pt-24">
        {/* Hero Section */}
        <section className="py-20 bg-gradient-to-br from-orange-50 to-white">
          <div className="max-w-7xl mx-auto px-6 text-center">
            <div className="inline-block text-sm font-semibold text-orange-600 bg-orange-100 px-4 py-2 rounded-full mb-4">
              CONTACTEZ-NOUS
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Parlons de votre projet
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Notre équipe est à votre écoute pour répondre à toutes vos questions
            </p>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Contact Information */}
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-8">
                  Contactez directement votre Président Manager
                </h2>

                <div className="space-y-6 mb-12">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-orange-100 rounded-xl flex-shrink-0">
                      <i className="ri-user-line text-2xl text-orange-600"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">Danko Jean-Luc</h3>
                      <p className="text-gray-600">Président Manager</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-green-100 rounded-xl flex-shrink-0">
                      <i className="ri-phone-line text-2xl text-green-600"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">Téléphone</h3>
                      <a href="tel:+32498664215" className="text-gray-600 hover:text-orange-600 transition-colors">
                        +32 498 66 42 15
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-blue-100 rounded-xl flex-shrink-0">
                      <i className="ri-mail-line text-2xl text-blue-600"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">Email</h3>
                      <a href="mailto:jld@danko.be" className="text-gray-600 hover:text-orange-600 transition-colors">
                        jld@danko.be
                      </a>
                    </div>
                  </div>
                </div>

                <div className="w-72 h-96 mx-auto rounded-2xl overflow-hidden shadow-lg">
                  <img
                    src="https://static.readdy.ai/image/9c58d8b9dc921ceebc49b67abc2b926b/ec27cb4e9d5ae875684ca61a2291a61e.png"
                    alt="Danko Jean-Luc - Président Manager"
                    className="w-full h-full object-cover object-top"
                  />
                </div>

                <div className="mt-8 bg-gradient-to-br from-orange-50 to-white rounded-2xl p-6">
                  <h3 className="font-semibold text-gray-900 mb-4 text-lg">Rejoignez notre équipe</h3>
                  <p className="text-gray-700 leading-relaxed">
                    N'hésite pas et rejoins notre équipe de consultants qui ont déjà pu changer leur futurs avec Smart SN.
                  </p>
                </div>
              </div>

              {/* Contact Form */}
              <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 shadow-lg">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  Envoyez-nous un message
                </h2>

                <form id="contact-form" onSubmit={handleSubmit} data-readdy-form>
                  <div className="space-y-6">
                    <div>
                      <label htmlFor="nom" className="block text-sm font-medium text-gray-700 mb-2">
                        Nom complet *
                      </label>
                      <input
                        type="text"
                        id="nom"
                        name="nom"
                        value={formData.nom}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                        placeholder="Votre nom"
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                        placeholder="votre@email.com"
                      />
                    </div>

                    <div>
                      <label htmlFor="telephone" className="block text-sm font-medium text-gray-700 mb-2">
                        Téléphone *
                      </label>
                      <input
                        type="tel"
                        id="telephone"
                        name="telephone"
                        value={formData.telephone}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                        placeholder="+32 XXX XX XX XX"
                      />
                    </div>

                    <div>
                      <label htmlFor="sujet" className="block text-sm font-medium text-gray-700 mb-2">
                        Sujet *
                      </label>
                      <select
                        id="sujet"
                        name="sujet"
                        value={formData.sujet}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                      >
                        <option value="">Sélectionnez un sujet</option>
                        <option value="telecom">Services Télécom</option>
                        <option value="energie">Énergie Verte</option>
                        <option value="consultant">Devenir Consultant</option>
                        <option value="autre">Autre demande</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                        Message *
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        maxLength={500}
                        rows={6}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm resize-none"
                        placeholder="Décrivez votre demande..."
                      ></textarea>
                      <p className="text-xs text-gray-500 mt-1">
                        {formData.message.length}/500 caractères
                      </p>
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full px-6 py-4 bg-orange-600 hover:bg-orange-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                    >
                      {isSubmitting ? 'Envoi en cours...' : 'Envoyer le message'}
                    </button>

                    {submitStatus === 'success' && (
                      <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                        <p className="text-green-800 text-sm">
                          ✓ Votre message a été envoyé avec succès! Nous vous répondrons dans les plus brefs délais.
                        </p>
                      </div>
                    )}

                    {submitStatus === 'error' && (
                      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-red-800 text-sm">
                          ✗ Une erreur est survenue. Veuillez réessayer ou nous contacter directement par téléphone.
                        </p>
                      </div>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* Map Section */}
        <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Notre Business Center
              </h2>
              <p className="text-xl text-gray-600 mb-4">
                Smart Services Network - Herstal
              </p>
              <div className="flex items-center justify-center space-x-2 text-gray-700">
                <i className="ri-map-pin-line text-orange-600"></i>
                <span className="font-medium">Rue de l'Abbaye 5, 4040 Herstal, Belgique</span>
              </div>
            </div>

            <div className="w-full h-96 rounded-2xl overflow-hidden shadow-xl">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2527.8!2d5.6!3d50.67!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47c0fa4e6e6e6e6e%3A0x6e6e6e6e6e6e6e6e!2sRue%20de%20l'Abbaye%205%2C%204040%20Herstal%2C%20Belgium!5e0!3m2!1sfr!2sbe!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Business Center Smart SN - Herstal"
              ></iframe>
            </div>
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
}